import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import * as foodService from '../services/foodService';
import './HistoryPage.css';

const HistoryPage = () => {
  const { tokens } = useAuth();
  const { t } = useLanguage();

  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const data = await foodService.getFoodHistory(tokens.access_token, 1, 50);
      setAnalyses(data);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (analysisId) => {
    if (!window.confirm(t('confirm_delete'))) {
      return;
    }

    try {
      await foodService.deleteFoodAnalysis(tokens.access_token, analysisId);
      setAnalyses(analyses.filter((a) => a.id !== analysisId));
      if (selectedAnalysis?.id === analysisId) {
        setShowModal(false);
        setSelectedAnalysis(null);
      }
    } catch (error) {
      console.error('Failed to delete analysis:', error);
      alert(t('delete_failed'));
    }
  };

  const handleToggleFavorite = async (analysisId, currentStatus) => {
    try {
      await foodService.toggleFavorite(tokens.access_token, analysisId, !currentStatus);
      setAnalyses(
        analyses.map((a) =>
          a.id === analysisId ? { ...a, is_favorite: !currentStatus } : a
        )
      );
      if (selectedAnalysis?.id === analysisId) {
        setSelectedAnalysis({ ...selectedAnalysis, is_favorite: !currentStatus });
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const handleViewDetails = (analysis) => {
    setSelectedAnalysis(analysis);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAnalysis(null);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="history-header">
        <h1>{t('food_history')}</h1>
        <p className="history-subtitle">{t('history_subtitle')}</p>
      </div>

      {analyses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>{t('no_history_yet')}</h3>
          <p>{t('no_history_desc')}</p>
        </div>
      ) : (
        <div className="history-grid">
          {analyses.map((analysis) => (
            <div key={analysis.id} className="history-card">
              {analysis.image_url && (
                <div className="history-image">
                  <img src={analysis.image_url} alt={analysis.food_name} />
                  <button
                    className={`favorite-btn ${analysis.is_favorite ? 'favorite-active' : ''}`}
                    onClick={() => handleToggleFavorite(analysis.id, analysis.is_favorite)}
                  >
                    {analysis.is_favorite ? '⭐' : '☆'}
                  </button>
                </div>
              )}

              <div className="history-content">
                <h3 className="history-title">{analysis.food_name}</h3>

                <div className="history-nutrition">
                  <div className="nutrition-badge">
                    <span className="badge-icon">🔥</span>
                    <span className="badge-value">
                      {analysis.nutrition?.calories || 0} {t('kcal')}
                    </span>
                  </div>
                  <div className="nutrition-badge">
                    <span className="badge-icon">💪</span>
                    <span className="badge-value">{analysis.nutrition?.protein || 0}g</span>
                  </div>
                  <div className="nutrition-badge">
                    <span className="badge-icon">🍞</span>
                    <span className="badge-value">{analysis.nutrition?.carbs || 0}g</span>
                  </div>
                  <div className="nutrition-badge">
                    <span className="badge-icon">🧈</span>
                    <span className="badge-value">{analysis.nutrition?.fat || 0}g</span>
                  </div>
                </div>

                <div className="history-date">
                  {new Date(analysis.created_at).toLocaleDateString()} •{' '}
                  {new Date(analysis.created_at).toLocaleTimeString()}
                </div>

                <div className="history-actions">
                  <button
                    className="btn-view"
                    onClick={() => handleViewDetails(analysis)}
                  >
                    {t('view_details')}
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(analysis.id)}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Details Modal */}
      {showModal && selectedAnalysis && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              ×
            </button>

            <div className="modal-body">
              {selectedAnalysis.image_url && (
                <div className="modal-image">
                  <img src={selectedAnalysis.image_url} alt={selectedAnalysis.food_name} />
                </div>
              )}

              <h2>{selectedAnalysis.food_name}</h2>

              <div className="modal-section">
                <h3>
                  <span className="section-icon">📊</span>
                  {t('nutrition_per_100g')}
                </h3>
                <div className="nutrition-grid">
                  <div className="nutrition-item">
                    <div className="item-label">{t('calories')}</div>
                    <div className="item-value">
                      {selectedAnalysis.nutrition?.calories || 0} {t('kcal')}
                    </div>
                  </div>
                  <div className="nutrition-item">
                    <div className="item-label">{t('protein')}</div>
                    <div className="item-value">
                      {selectedAnalysis.nutrition?.protein || 0}g
                    </div>
                  </div>
                  <div className="nutrition-item">
                    <div className="item-label">{t('carbs')}</div>
                    <div className="item-value">
                      {selectedAnalysis.nutrition?.carbs || 0}g
                    </div>
                  </div>
                  <div className="nutrition-item">
                    <div className="item-label">{t('fat')}</div>
                    <div className="item-value">{selectedAnalysis.nutrition?.fat || 0}g</div>
                  </div>
                </div>
              </div>

              {selectedAnalysis.recipe && (
                <>
                  {selectedAnalysis.recipe.ingredients && (
                    <div className="modal-section">
                      <h3>
                        <span className="section-icon">🥘</span>
                        {t('ingredients')}
                      </h3>
                      <ul className="ingredients-list">
                        {selectedAnalysis.recipe.ingredients.map((ingredient, index) => (
                          <li key={index}>{ingredient}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedAnalysis.recipe.instructions && (
                    <div className="modal-section">
                      <h3>
                        <span className="section-icon">📝</span>
                        {t('recipe')}
                      </h3>
                      <ol className="instructions-list">
                        {selectedAnalysis.recipe.instructions.map((instruction, index) => (
                          <li key={index}>{instruction}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {selectedAnalysis.recipe.cooking_time && (
                    <div className="cooking-time">
                      ⏱️ {t('cooking_time')}: {selectedAnalysis.recipe.cooking_time}{' '}
                      {t('minutes')}
                    </div>
                  )}
                </>
              )}

              {selectedAnalysis.youtube_video_url && (
                <div className="modal-section">
                  <h3>
                    <span className="section-icon">🎥</span>
                    {t('cooking_video')}
                  </h3>
                  <a
                    href={selectedAnalysis.youtube_video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="youtube-link"
                  >
                    {t('watch_on_youtube')} →
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
